import { RouteRecordRaw } from 'vue-router';
import { PageName } from '@/router/routes/constants';
import { isDevMode, isDevOrStageMode } from '@/core/environmentService';
import dictionaryService from '@/core/translation/dictionary.service';
import siteService from '@/project/site/site.service';
import serverContextService from '@/core/serverContext.service';
import { SiteName } from '@/types/apiServerContract';
import { hardRedirect } from '@/core/redirect.service';
import domService from '@/core/dom/dom.service';
import HttpStatus from 'http-status-codes';

const GeneralRoutes: RouteRecordRaw[] = [
    {
        path: '/:pathMatch(.*)*-wid-:id',
        name: PageName.PRODUCT_DETAILS,
        component: () => import(
            '@/project/app/client-pages/product-details/ProductDetailsPage.vue'),
        meta: {
            headMeta: {
                index: serverContextService.siteName !== SiteName.EetRetail
            },
            isPublic: true,
            skipAutoPageTracking: true,
            skipAutoBreadcrumb: true,
            skipAutoSeoTitle: true
        }
    },
    {
        path: '/campaigns/:campaignCode',
        name: PageName.CAMPAIGN,
        component: () => import(
            '@/project/app/client-pages/CampaignPage.vue'),
        props: (route) => ({
            campaignCode: route.params.campaignCode
        }),
        children: [{
            path: '/campaigns/:pathMatch(.*)*/:campaignCode',
            name: PageName.CAMPAIGN_2,
            component: () => import(
                '@/project/app/client-pages/CampaignPage.vue'),
            props: (route) => ({
                campaignCode: route.params.campaignCode
            }),
            meta: {
                headMeta: {
                    index: serverContextService.siteName !== SiteName.EetRetail
                },
                isPublic: true,
                skipAutoPageTracking: true,
                skipAutoBreadcrumb: true,
                skipAutoSeoTitle: true
            }
        }],
        meta: {
            headMeta: {
                index: serverContextService.siteName !== SiteName.EetRetail
            },
            isPublic: true,
            skipAutoPageTracking: true,
            skipAutoBreadcrumb: true,
            skipAutoSeoTitle: true
        }
    },
    {
        path: '/brands/all',
        redirect: to => {
            domService.setPrerenderHeader(`Location: ${location.origin}/${siteService.getCulture()}/brands`);
            domService.setPrerenderStatusCode(HttpStatus.MOVED_PERMANENTLY);
            return { name: PageName.BRAND_OVERVIEW };
        }
    },
    {
        path: '/brands',
        name: PageName.BRAND_OVERVIEW,
        component: () => import(
            '@/project/app/client-pages/BrandsOverview.vue'),
        meta: {
            isPublic: true,
            headMeta: {
                index: serverContextService.siteName !== SiteName.EetRetail
            },
            breadcrumb: [
                {
                    name: dictionaryService.get('Brand.BrandOverview.AllBrands'),
                    page: PageName.BRAND_OVERVIEW
                }
            ]
        }
    },
    {
        path: '/brands/:brandName',
        name: PageName.BRAND,
        component: () => import(
            '@/project/app/client-pages/BrandPage.vue'),
        props: (route) => ({
            brandName: route.params.brandName,
            mode: PageName.BRAND
        }),
        meta: {
            headMeta: {
                index: serverContextService.siteName !== SiteName.EetRetail
            },
            isPublic: true,
            skipAutoSeoTitle: true,
            skipAutoPageTracking: true
        }
    },
    {
        path: '/brands/:brandName/:brandSubContent',
        name: PageName.BRAND_SUBCONTENT,
        component: () => import(
            '@/project/app/client-pages/BrandPage.vue'),
        props: (route) => ({
            brandName: route.params.brandName,
            mode: PageName.BRAND_SUBCONTENT,
            brandSubContent: route.params.brandSubContent
        }),
        meta: {
            headMeta: {
                index: serverContextService.siteName !== SiteName.EetRetail
            },
            isPublic: true,
            skipAutoSeoTitle: true,
            skipAutoPageTracking: true
        }
    },
    {
        path: '/c-:categoryId(uncategorized|\\d{2,})',
        name: PageName.CATEGORY,
        component: () => import('@/project/app/client-pages/CategoryPage.vue'),
        beforeEnter(to, from, next) {
            hardRedirect(to.fullPath);
        },
        meta: {
            isPublic: true
        }
    },
    {
        path: '/:pathMatch(.*)*/c-:categoryId(uncategorized|\\d{2,})',
        name: PageName.CATEGORY_2,
        component: () => import('@/project/app/client-pages/CategoryPage.vue'),
        props: (route) => ({
            categoryId: route.params.categoryId,
            mode: PageName.CATEGORY
        }),
        meta: {
            headMeta: {
                index: serverContextService.siteName !== SiteName.EetRetail
            },
            isPublic: true,
            skipAutoPageTracking: true,
            skipAutoBreadcrumb: true,
            skipAutoSeoTitle: true
        }
    },
    {
        path: '/plists',
        name: PageName.PUBLIC_PRODUCT_LISTS,
        component: () => import(
            '@/project/app/client-pages/my-account/product-lists/ProductAssortment.vue'),
        meta: {
            isPublic: false,
            breadcrumb: [
                {
                    name: dictionaryService.get('AssortmentLists.Details'),
                    page: PageName.PUBLIC_PRODUCT_LISTS
                }
            ]
        }
    },
    {
        path: '/search/:term?',
        name: PageName.SEARCH,
        component: () => import('@/project/app/client-pages/SearchPage.vue'),
        props: (route) => ({
        }),
        meta: {
            headMeta: {
                index: true
            },
            hideBreadcrumb: true,
            isPublic: true,
            skipAutoPageTracking: true,
            skipAutoBreadcrumb: true,
            skipAutoSeoTitle: true,
            transition: 'slide-right'
        }
    },
    {
        path: '/basket',
        name: PageName.BASKET,
        component: () => import(
            '@/project/app/client-pages/basket-page/BasketPage.vue'),
        meta: {
            headMeta: {},
            isPublic: false,
            skipAutoPageTracking: true,
            breadcrumb: [
                {
                    name: dictionaryService.get('Basket'),
                    page: PageName.BASKET
                }
            ]
        }
    },
    {
        path: '/checkout',
        name: PageName.CHECKOUT,
        component: () => import(
            '@/project/app/client-pages/checkout/CheckoutPage.vue'),
        meta: {
            isTunnelView: true,
            hideBreadcrumb: true,
            isPublic: false,
            skipAutoPageTracking: true
        }
    },
    {
        path: '/confirmation',
        name: PageName.CONFIRMATION,
        component: () => import(
            '@/project/app/client-pages/confirmation/ConfirmationPage.vue'),
        meta: {
            hideBreadcrumb: true,
            isPublic: false,
            skipAutoPageTracking: true
        }
    },
    {
        path: '/404',
        name: 'notfound',
        component: () => import('@/project/app/content-pages/ContentPageRenderer.vue'),
        meta: {
            isContent: true,
            isPublic: true
        }

    },
    {
        path: '/500',
        name: 'errorpage',
        component: () => import('@/project/app/content-pages/ContentPageRenderer.vue'),
        meta: {
            isContent: true,
            isPublic: true
        }

    },
    // TODO: the old redirects do not look like they work anymore, time for removal?
    // {
    //     path: '/page/:pageId(\\d+)',
    //     name: 'pageRedirect',
    //     beforeEnter(to) { location.href = `/${siteService.getCulture()}/${to.params.pageId}`; },
    //     meta: {
    //         isPublic: true,
    //         skipAutoPageTracking: true
    //     }
    //
    // },
    // {
    //     path: '/:pageId(\\d+)',
    //     name: 'pageIdRedirect',
    //     beforeEnter(to) { location.href = `/${siteService.getCulture()}/${to.params.pageId}`; },
    //     meta: {
    //         isPublic: true,
    //         skipAutoPageTracking: true
    //     }
    // },
    {
        path: '/brand-product-guide-overlay',
        name: 'brandProductGuideOverlay',
        props: (route) => ({
            brandName: route.query.productGuideBrandName
        }),
        component: () => import('@/project/app/blocks/productguide/StepProductGuide/Overlay/BrandProductGuidePLPOverlay.vue'),
        meta: {
            isPublic: true,
            hideBreadcrumb: true,
            isTunnelView: true
        }
    },
    {
        path: '/banner-preview',
        name: PageName.MY_ACCOUNT_BANNER_PREVIEW,
        component: () => import(
            '@/project/app/client-pages/my-account/banner-preview/BannerPreview.vue'),
        meta: {
            isPublic: true,
            headMeta: {
                index: false
            }
        }
    },
    {
        path: '/product-compare/:products*',
        name: PageName.PRODUCT_COMPARE,
        component: () => import(
            '@/project/app/client-pages/product-details/ProductComparePage.vue'),
        meta: {
            breadcrumb: [
                {
                    name: dictionaryService.get('Breadcrumbs.ProductCompare'),
                    page: PageName.PRODUCT_COMPARE
                }
            ],
            isPublic: true,
            headMeta: {
                index: false
            }
        }
    },
];

const kitchenSinkChildren: RouteRecordRaw[] = [];
kitchenSinkChildren.push(
    {
        path: '/sandbox/kitchensink/forms',
        name: PageName.KITCHEN_SINK_FORMELEMENTS,
        meta: {
            isPublic: false,
            skipAutoPageTracking: true,
            headMeta: {
                index: false
            },
            skipAutoBreadcrumb: true,
            skipAutoSeoTitle: true
        },
        component: () => import('@/project/kitchensink/KitchenSinkForms.vue')
    },
);

const devModeKitchenSinkChildren: RouteRecordRaw[] = []
if (isDevMode()) {
    devModeKitchenSinkChildren.push(
        {
            path: '/sandbox/kitchensink/video',
            name: PageName.KITCHEN_SINK_VIDEOEMBED,
            meta: {
                isPublic: false,
                skipAutoPageTracking: true,
                headMeta: {
                    index: false
                },
                skipAutoBreadcrumb: true,
                skipAutoSeoTitle: true
            },
            component: () => import('@/project/kitchensink/KitchenSinkVideoEmbed.vue')
        });
    devModeKitchenSinkChildren.push(
        {
            path: '/sandbox/kitchensink/contentblocks',
            name: PageName.KITCHEN_SINK_CONTENTBLOCKS,
            meta: {
                isPublic: false,
                skipAutoPageTracking: true,
                headMeta: {
                    index: false
                },
                skipAutoBreadcrumb: true,
                skipAutoSeoTitle: true
            },
            component: () => import('@/project/kitchensink/KitchenSinkContentFAQ.vue')
        }
    );
}
if (isDevOrStageMode()) {
    GeneralRoutes.push({
        path: '/sandbox/kitchensink',
        name: PageName.KITCHEN_SINK,
        meta: {
            isPublic: false,
            skipAutoPageTracking: true,
            headMeta: {
                index: false
            },
            skipAutoBreadcrumb: true,
            skipAutoSeoTitle: true
        },
        component: () => import(
            '@/project/app/client-pages/KitchenSinkPage.vue'),
        children: [
            ...kitchenSinkChildren,
            ...devModeKitchenSinkChildren
        ]
    });
}

export { GeneralRoutes };
